import type { ColDef, ValueGetterParams } from 'ag-grid-community'
import colors from '@sportninja/common/constants/appColors'
import { formatCurrency } from '@sportninja/common/utils/utils'
import { isDivisionTeamFull } from '@sportninja/common/features/registration/startStep/divisionRole'
import {
  isDraftTeam,
  isTeamRosterFull,
  teamMaxPlayers,
  TEAM_FULL_MESSAGE,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import type {
  DivisionTeam,
  StartRole,
} from '@sportninja/common/features/registration/startStep/types'
import type { Division } from '@sportninja/common/types'
import TeamNameCell from '../components/TeamNameCell'

/**
 * Columns for the list view of the two entity tabs.
 *
 * The cards and this grid answer the same question, so they agree on what is
 * worth showing and on how each value reads — the fee halves follow the role,
 * a full division says so, a draft is marked. What differs is density: a card
 * can afford a four-figure fee block, a row cannot, so each role sees only its
 * own fee and deposit.
 *
 * Pure column definitions, deliberately free of React: the grid component wires
 * them to `DataGrid`, and these stay testable on their own.
 */

/** Absent rather than zero. A repeated "N/A" down a column reads as noise. */
const EMPTY = '—'

/** A row in the divisions grid is just the division. */
export type DivisionRow = Division

/** A row in the teams grid carries its division, as the tab's index does. */
export type TeamRow = DivisionTeam<Division>

const currencyOrEmpty = (
  value: number | string | null | undefined,
  currencyName: string
): string => {
  const numeric = Number(value)
  if (!value || Number.isNaN(numeric) || numeric <= 0) {
    return EMPTY
  }
  return formatCurrency(numeric, currencyName)
}

const textOrEmpty = (value: string | null | undefined): string => {
  const trimmed = value?.trim()
  // 'N/A' is a real value in `division_gender` / `day_of_week`, but it is not
  // an answer anyone is looking for in a column.
  if (!trimmed || trimmed === 'N/A') {
    return EMPTY
  }
  return trimmed
}

/**
 * How much room is left in a division.
 *
 * `max_teams` is null for an uncapped division, which is the common case in real
 * payloads — so "unlimited" is stated rather than computed into a negative.
 */
export const divisionCapacityLabel = (division: Division): string => {
  const enrolled = division.team_count_enrolled ?? 0
  const max = division.max_teams
  if (max == null) {
    return `${enrolled} Registered`
  }
  return `${enrolled} / ${max} Registered`
}

/**
 * The state that decides whether a row can be picked at all.
 *
 * Ordered by what stops the reader soonest: a closed registration outranks a
 * full one, because a full division that is also closed is not "nearly
 * available".
 */
export const divisionStatusLabel = (
  division: Division,
  role: StartRole | null
): string => {
  const playerOffered = Boolean(division.is_player_registration)
  const teamOffered = Boolean(division.is_team_registration)
  const offered = role === 'player' ? playerOffered : teamOffered

  if (!playerOffered && !teamOffered) {
    return 'Registration Closed'
  }
  if (role && !offered) {
    return 'Not Open To You'
  }
  if (isDivisionTeamFull(division)) {
    return 'Division Full'
  }
  return 'Open'
}

/**
 * What the row IS, not how full it is.
 *
 * Availability is a separate fact and belongs to the roster column, which shows
 * the counts. Folding the two together meant a draft that filled up stopped
 * reading as a draft — the category silently replaced by a condition.
 */
export const teamTypeLabel = (entry: TeamRow): string =>
  isDraftTeam(entry.team) ? 'Draft' : 'Team'

/**
 * Roster occupancy as a column.
 *
 * Shorter than `teamRosterLabel`'s sentence: the header carries the noun, so
 * repeating "Players Registered" on every row would be wasted width.
 */
export const teamRosterCell = (entry: TeamRow): string => {
  const count = entry.team?.player_count
  if (typeof count !== 'number') {
    return EMPTY
  }
  const max = teamMaxPlayers(entry.team)
  return max == null ? `${count}` : `${count} / ${max}`
}

/**
 * Division columns, narrowed to the reader's own role.
 *
 * A player has no use for team fees and a rep none for player fees, so only one
 * pair is built — matching how the card hides the irrelevant half rather than
 * showing four amounts nobody asked for.
 */
export const buildDivisionColumns = (
  role: StartRole | null,
  currencyName: string
): ColDef<DivisionRow>[] => {
  const isTeamRole = role === 'team'
  const feeHeader = isTeamRole ? 'Team Fee' : 'Player Fee'
  const depositHeader = isTeamRole ? 'Team Deposit' : 'Player Deposit'

  return [
    {
      colId: 'name',
      headerName: 'Division',
      //  Deliberately NO `field`. A column carrying both a field and a
      //  valueGetter renders from the getter but still takes part in
      //  field-based change detection, so a recycled cell can be judged
      //  unchanged and left blank until something forces it to redraw.
      //
      //  No `flex` either: columns run at their own width from the left, and
      //  the LAST one absorbs whatever the table has left over. A flex here
      //  would pull that slack to the front instead.
      minWidth: 220,
      valueGetter: ({ data }) => textOrEmpty(data?.name),
      //  The DESCRIPTION, not the name — the name is already the cell, and a
      //  tooltip repeating it says nothing. The card shows the description
      //  clamped to three lines; here it is the only place it can appear at
      //  all. Empty when the division has none, which renders no tooltip
      //  rather than an empty bubble.
      tooltipValueGetter: ({ data }) => data?.description?.trim() ?? '',
    },
    {
      colId: 'gender',
      headerName: 'Gender',
      //  Sized to the HEADER, which is longer than any value it holds ("Male",
      //  "Female", "Coed"). What these two give up goes to the division name,
      //  the only flex column and the one that actually runs out of room.
      //
      //  Pinned: min and max together fix the width, so this column cannot take
      //  space from the name if it is ever given a flex.
      minWidth: 120,
      maxWidth: 120,
      valueGetter: ({ data }) => textOrEmpty(data?.division_gender?.name),
    },
    {
      colId: 'age',
      headerName: 'Age',
      //  Holds a bare number ("25"); the header and sort icon set the floor.
      minWidth: 120,
      maxWidth: 120,
      valueGetter: ({ data }) => textOrEmpty(data?.division_age_v2),
    },
    {
      colId: 'day',
      headerName: 'Day',
      //  Third of the attribute trio, in the same order the card's detail row
      //  reads: gender, age, day. Pinned like its neighbours so none of them
      //  takes width from the name.
      minWidth: 120,
      maxWidth: 120,
      valueGetter: ({ data }) => textOrEmpty(data?.division_day_of_week_v2),
      //  The field is free text and can name several days, which a pinned
      //  120px column cuts off. The tooltip carries the whole value.
      //
      //  The RAW string, not `textOrEmpty`: the cell's em-dash stands in for
      //  an absent value, and a tooltip reading "—" would say less than none
      //  at all. Empty renders no bubble.
      tooltipValueGetter: ({ data }) =>
        data?.division_day_of_week_v2?.trim() ?? '',
    },
    {
      colId: 'fee',
      headerName: feeHeader,
      minWidth: 130,
      valueGetter: ({ data }) =>
        currencyOrEmpty(
          isTeamRole ? data?.team_cost : data?.player_cost,
          currencyName
        ),
    },
    {
      colId: 'deposit',
      headerName: depositHeader,
      minWidth: 140,
      //  The trailing column for a PLAYER, so it takes the leftover width. A
      //  rep has Teams after this, which takes it instead.
      ...(isTeamRole ? {} : { flex: 1 }),
      valueGetter: ({ data }) => {
        // A deposit that is not required is not a smaller deposit — it is none.
        const required = isTeamRole
          ? data?.deposit_required
          : data?.player_deposit_required
        if (!required) {
          return EMPTY
        }
        return currencyOrEmpty(
          isTeamRole ? data?.deposit : data?.player_deposit,
          currencyName
        )
      },
    },
    //  How many teams have registered is a REP's question — it says whether
    //  there is room to bring one. A player joins a specific roster, picked on
    //  the other tab, so the count answers nothing they are deciding. The card
    //  already hides its "N Teams Registered" badge for a player; the column
    //  follows.
    ...(isTeamRole
      ? [
          {
            colId: 'capacity',
            headerName: 'Teams',
            minWidth: 130,
            //  Last column for a rep: it soaks up the remaining width.
            flex: 1,
            valueGetter: ({ data }: ValueGetterParams<DivisionRow>) =>
              data ? divisionCapacityLabel(data) : EMPTY,
          },
        ]
      : []),
  ]
}

/**
 * Team columns.
 *
 * The division is a column here rather than a filter, because the teams tab can
 * list the whole competition — a team name alone does not say what the reader
 * would be registering into.
 */
export const buildTeamColumns = (): ColDef<TeamRow>[] => [
  {
    colId: 'name',
    headerName: 'Team / Draft',
    //  No `flex`: like the divisions grid, columns sit at their own width from
    //  the left and the trailing Division column takes the slack.
    minWidth: 240,
    //  Draws the crest beside the name. The valueGetter stays load-bearing —
    //  sorting and the quick filter read through it, not through the cell.
    cellRenderer: TeamNameCell,
    valueGetter: ({ data }) => textOrEmpty(data?.team?.name),
    //  A full team says WHY it cannot be picked; everything else falls back to
    //  its own name, which is what the tooltip is for when the cell truncates.
    tooltipValueGetter: ({ data }) =>
      data && isTeamRosterFull(data.team)
        ? TEAM_FULL_MESSAGE
        : data?.team?.name ?? '',
  },
  {
    colId: 'type',
    //  Sits tight against the name it qualifies. Holds "Team" or "Draft", so
    //  the header is the widest thing in it.
    headerName: 'Type',
    minWidth: 120,
    maxWidth: 120,
    valueGetter: ({ data }) => (data ? teamTypeLabel(data) : EMPTY),
  },
  {
    colId: 'roster',
    //  Ahead of the division: how full a team is bears on whether to pick it,
    //  while the division is context the reader may already have fixed.
    //  Narrower than its own header — the values are short ("2 / 20"), so the
    //  header is what gives, not the data.
    headerName: 'Players Registered',
    minWidth: 130,
    valueGetter: ({ data }) => (data ? teamRosterCell(data) : EMPTY),
    //  A full roster turns red, matching the card's own count. It is the reason
    //  the row is unselectable, so it should be the thing that stands out —
    //  `cellStyle` rather than a renderer, since only the color changes.
    cellStyle: ({ data }) =>
      data && isTeamRosterFull(data.team)
        ? { color: colors.ERROR_200 }
        : undefined,
  },
  {
    colId: 'division',
    //  Trailing flex column: it takes whatever the fixed columns leave.
    headerName: 'Division',
    flex: 1,
    minWidth: 140,
    valueGetter: ({ data }) => textOrEmpty(data?.division?.name),
  },
]
